"use client";

import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslations, useLocale } from "next-intl";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetPublicAiReportsQuery } from "@/api/reports/publicReportApi";
import RiskLevelCard from "@/components/report/RiskLevelCard";
import ReportVerdictGate from "@/components/report/ReportVerdictGate";
import { useReviewerAccess } from "@/components/report/useReviewerAccess";
import SparkleIcon from "@/components/icons/generated/SparkleIcon";
import TrustIndexLayout from "@/app/assignments/[id]/components/DataTable/StudentDrawerContent/tabs/ReportTab/components/TrustIndexSection/TrustIndexLayout";
import ReportListItem from "@/app/assignments/[id]/components/DataTable/StudentDrawerContent/tabs/ReportTab/components/TrustIndexSection/ReportListItem";
import { getTrustReportTypes, REPORT_TYPE_CONFIGS } from "@/app/assignments/[id]/components/DataTable/StudentDrawerContent/tabs/ReportTab/constants/reportTypes";
import AIChartsPanelModal from "@/app/assignments/[id]/components/DataTable/StudentDrawerContent/tabs/ReportTab/components/TrustIndexSection/AIChartsPanel";
import { usePipelineSSE } from "@/app/assignments/[id]/components/DataTable/StudentDrawerContent/tabs/ReportTab/hooks/usePipelineSSE";
import PipelineLoader from "@/app/assignments/[id]/components/DataTable/StudentDrawerContent/tabs/ReportTab/components/PipelineLoader";
import { PipelineReportsContext } from "@/app/assignments/[id]/components/DataTable/StudentDrawerContent/tabs/ReportTab/hooks/useReportData";
import {
  getRiskLevelFromScore,
  getRiskLevelStyles,
} from "@/app/assignments/[id]/components/DataTable/StudentDrawerContent/tabs/ReportTab/constants/riskConfig";
import type {
  EvaluationReportData,
  BaseAiReportData,
} from "@/types/endpoints/reports/GetAiReportById";
import type { SummaryContent } from "@/types/endpoints/reports/GetAiReportById";
import type { PublicReportsData } from "@/types/endpoints/reports/GetPublicAiReports";
import type { AiReportType } from "@/api/reports/reportApi";
import type { WebinarRecord } from "@/types/endpoints/assignments/GetWebinarSessions";
import type { CredibilityVerdict } from "@/types/endpoints/assignments/GetAssignmentAttemptDetail";
import { isManualReportGeneration } from "@/lib/branding";

interface PublicReportSectionProps {
  attemptId: number;
  credibility?: number;
  webinarRecords?: WebinarRecord[];
  resultsChart?: Array<{ name: string; data: number[] }>;
  assignmentType?: string;
  credibilityVerdict?: CredibilityVerdict | null;
}

const PUBLIC_BASE_REPORT_TYPES: Array<AiReportType> = [
  "actions",
  "answers",
  "q_density",
  "transcribe",
];

function extractSummaryText(summary: SummaryContent | undefined | null): string {
  if (!summary) return "";
  if (typeof summary === "string") return summary;
  if (typeof summary === "object" && summary !== null && "content" in summary) {
    return String((summary as { content: string }).content);
  }
  return String(summary);
}

function getLocalizedSummary(
  data: EvaluationReportData,
  locale: string
): SummaryContent | null {
  switch (locale) {
    case "kk":
      return data.summary_kz || data.summary_ru || null;
    case "en":
      return data.summary_en || data.summary_ru || null;
    case "ru":
    default:
      return data.summary_ru || null;
  }
}

const ConclusionSkeleton = memo(function ConclusionSkeleton() {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-5 w-28" />
      </CardHeader>
      <CardContent className="space-y-3">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </CardContent>
    </Card>
  );
});

const PublicReportSkeleton = memo(function PublicReportSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <div className="border rounded-lg p-6 flex flex-col gap-2">
        <Skeleton className="h-[60px] w-full rounded-[50px]" />
        <div className="flex gap-10 py-6">
          <div className="flex-1 flex flex-col gap-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
          <Skeleton className="w-[200px] h-[200px] rounded-full" />
        </div>
      </div>
      <ConclusionSkeleton />
    </div>
  );
});

const PublicReportSection = memo(function PublicReportSection({
  attemptId,
  credibility,
  webinarRecords,
  resultsChart,
  assignmentType,
  credibilityVerdict,
}: PublicReportSectionProps) {
  const t = useTranslations();
  const locale = useLocale();
  const { canReview } = useReviewerAccess();

  const manual = isManualReportGeneration();

  // Публичный источник — показывает УЖЕ готовый отчёт (работает без авторизации).
  const {
    data: baseData,
    isLoading: isBaseLoading,
    isFetching: isBaseFetching,
    isError: isBaseError,
    refetch: refetchBase,
  } = useGetPublicAiReportsQuery({
    attemptId,
    reportTypes: PUBLIC_BASE_REPORT_TYPES,
  });

  const { data: evaluationResponse, refetch: refetchEvaluation } =
    useGetPublicAiReportsQuery({
      attemptId,
      reportTypes: ["evaluation"],
      // Заключение (summary) бэк собирает на основе base-отчётов.
      baseReportTypes: PUBLIC_BASE_REPORT_TYPES,
    });

  // Генерация — тот же SSE-пайплайн, что и в менеджерском дравере (ReportTab):
  // фазы/percent + видео-лоадер. Доступна залогиненному проверяющему (canReview →
  // есть Bearer-токен для стрима /pipeline/{id}/stream). Логика идентична драверу.
  const pipeline = usePipelineSSE(attemptId);
  const [useStreamLocal, setUseStreamLocal] = useState(false);
  const isGenerating =
    pipeline.phase === "running" || pipeline.phase === "finishing";

  const pipelineActive = pipeline.phase !== "idle" && pipeline.phase !== "error";
  const useStream = useStreamLocal || pipelineActive;

  useEffect(() => {
    if (pipeline.phase === "done" && useStreamLocal) {
      refetchBase();
      refetchEvaluation();
    }
  }, [pipeline.phase, useStreamLocal, refetchBase, refetchEvaluation]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState<AiReportType | undefined>(undefined);

  const openReportModal = useCallback((type: AiReportType) => {
    if (type !== "answers" && type !== "actions" && type !== "transcribe") return;
    setModalTab(type);
    setModalOpen(true);
  }, []);

  // Клик «Сгенерировать» / повторная генерация — запуск стрима (как regenerate в ReportTab).
  const startGeneration = useCallback(
    (force: boolean) => {
      setUseStreamLocal(true);
      pipeline.start(force);
    },
    [pipeline]
  );

  // Во время/после прогона данные пайплайна имеют приоритет над публичными.
  const reports = useMemo<PublicReportsData>(() => {
    const publicReports = {
      ...(baseData?.reports ?? {}),
      ...(evaluationResponse?.reports ?? {}),
    };
    return (
      useStream ? { ...publicReports, ...pipeline.reports } : publicReports
    ) as PublicReportsData;
  }, [baseData?.reports, evaluationResponse?.reports, useStream, pipeline.reports]);

  const reportErrors = useMemo(
    () => ({
      ...(baseData?.errors ?? {}),
      ...(evaluationResponse?.errors ?? {}),
    }),
    [baseData?.errors, evaluationResponse?.errors]
  );
  const evaluationData = reports?.evaluation;

  // «Готово» — как в дравере (ReportTab): во время прогона стрима завершение
  // определяет фаза пайплайна (done наступает после видео-лоадера), а для показа
  // уже готового публичного отчёта — наличие evaluation.
  const isGenerated = useStream
    ? pipeline.phase === "done"
    : !!evaluationData && !reportErrors?.evaluation;

  // Видео-лоадер генерации — только examru (manual) во время активного стрима.
  const showVideoLoader = manual && useStream && isGenerating;

  // «Балл по логам» = credibility попытки из Laravel. null → -1 (sentinel «—»), как в дравере.
  const baseLoading = isBaseLoading || isBaseFetching;
  const logCredibility =
    typeof credibility === "number" && credibility >= 0
      ? credibility
      : credibilityVerdict?.credibility ?? -1;
  const displayScore =
    evaluationData?.trust_score ?? (baseLoading ? undefined : logCredibility);

  const listReports = useMemo(() => {
    return getTrustReportTypes(assignmentType).map((config) => {
      const reportData = reports?.[config.type] as BaseAiReportData | undefined;
      const hasError = !!reportErrors?.[config.type];
      return {
        type: config.type,
        config,
        data: reportData,
        isError: hasError,
      };
    });
  }, [reports, reportErrors, assignmentType]);

  const circleSegments = useMemo(() => {
    return listReports
      .filter((r) => r.data && !r.isError)
      .map((r) => ({
        value: r.data?.risk?.score || 0,
        label: r.config.labelKey,
      }))
      .sort((a, b) => b.value - a.value);
  }, [listReports]);

  const localizedSummary = useMemo(() => {
    if (!evaluationData) return null;
    return getLocalizedSummary(evaluationData, locale);
  }, [evaluationData, locale]);

  const summaryText = useMemo(
    () => extractSummaryText(localizedSummary),
    [localizedSummary]
  );

  const paragraphs = useMemo(() => {
    if (!summaryText) return [];
    return summaryText
      .split(/\n\n|\n|;/)
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
  }, [summaryText]);

  const riskLevel = useMemo(
    () => getRiskLevelFromScore(displayScore ?? 100),
    [displayScore]
  );
  const riskStyles = useMemo(() => getRiskLevelStyles(riskLevel), [riskLevel]);

  // Данные для дочерних блоков модалки (useReportData) — из активного источника,
  // как PipelineReportsContext в менеджерском ReportTab.
  const pipelineReportsValue = useMemo(
    () => ({ reports: reports as Record<string, unknown>, isGenerating }),
    [reports, isGenerating]
  );

  // Полный скелетон — только на первичной загрузке публичных данных (стрим не запущен).
  if (baseLoading && !useStream) {
    return <PublicReportSkeleton />;
  }

  if (isBaseError && !useStream) {
    return (
      <Card className="p-6">
        <p className="text-muted-foreground text-center">
          {t("label-report-unavailable")}
        </p>
      </Card>
    );
  }

  const conclusionTitle = t("label-evaluation-report");

  return (
    <PipelineReportsContext.Provider value={pipelineReportsValue}>
      <div className="flex flex-col gap-4">
        <TrustIndexLayout
          blurClass={
            showVideoLoader
              ? "blur-[2px] opacity-50 pointer-events-none select-none"
              : ""
          }
          videoLoaderMode={showVideoLoader}
          riskCard={
            <RiskLevelCard
              score={displayScore}
              isLoading={typeof displayScore !== "number"}
              onRefresh={
                !isGenerated || isGenerating
                  ? undefined
                  : () => startGeneration(true)
              }
              isRefreshing={isGenerating}
              showIcon={manual ? isGenerated : true}
            />
          }
          circle={{
            segments: isGenerated ? circleSegments : [],
            totalValue: displayScore,
            totalLabel: t("label-points"),
            title: isGenerated ? t("label-trust-index") : t("label-log-score"),
            hasTrustScore: isGenerated,
            showSparkle: manual,
            ariaLabel: isGenerated ? t("label-trust-index") : t("label-log-score"),
            onClick: isGenerated ? () => openReportModal("actions") : undefined,
          }}
          circleSlot={
            showVideoLoader ? (
              <>
                <PipelineLoader
                  percent={pipeline.percent}
                  onComplete={() => pipeline.complete()}
                />
                <p className="mt-3 text-center text-[10px] text-gray-500">
                  {t("pipeline-title")}
                </p>
              </>
            ) : undefined
          }
        >
          {isGenerated ? (
            listReports.map((report) => (
              <ReportListItem
                key={report.type}
                config={report.config}
                isLoading={false}
                isError={report.isError || !report.data}
                onClick={() => openReportModal(report.type)}
              />
            ))
          ) : isGenerating && !showVideoLoader ? (
            listReports.map((report) => (
              <ReportListItem
                key={report.type}
                config={report.config}
                isLoading
                isError={false}
              />
            ))
          ) : (
            <>
              <ReportListItem
                config={REPORT_TYPE_CONFIGS.actions}
                isLoading={false}
                isError={false}
                onClick={() => openReportModal("actions")}
              />
              {canReview && manual && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => startGeneration(false)}
                  disabled={isGenerating}
                  className="w-full justify-start text-xs px-0.5 py-0 h-9 rounded-sm border-0 bg-gradient-to-r from-[#0059FF] to-[#B20BFF] text-white hover:opacity-95 hover:text-white"
                >
                  <SparkleIcon className="h-3.5 w-3.5 shrink-0" />
                  {t("btn-generate-ai-proctor")}
                </Button>
              )}
            </>
          )}
        </TrustIndexLayout>

      {modalOpen && (
        <AIChartsPanelModal
          open={modalOpen}
          attemptId={attemptId}
          initialTab={modalTab}
          onClose={() => setModalOpen(false)}
          webinarRecordsOverride={webinarRecords}
          resultsChartOverride={resultsChart}
          assignmentType={assignmentType}
          credibilityVerdict={credibilityVerdict}
          onlyTab={isGenerated ? undefined : "actions"}
        />
      )}

      <ReportVerdictGate attemptId={attemptId} />

      {paragraphs.length > 0 && (
        <Card
          className="flex flex-col gap-2! p-4!"
          style={{
            borderColor: riskStyles.border,
            backgroundColor: riskStyles.bg,
          }}
        >
          <CardHeader className="p-0! gap-0!">
            <CardTitle className="text-base font-semibold">
              {conclusionTitle}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0!">
            <ul className="list-disc pl-5 space-y-2 text-sm text-foreground leading-relaxed">
              {paragraphs.map((paragraph, index) => (
                <li
                  key={index}
                  dangerouslySetInnerHTML={{
                    __html: paragraph.replace(
                      /\*\*(.*?)\*\*/g,
                      '<strong class="font-semibold">$1</strong>'
                    ),
                  }}
                />
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
      </div>
    </PipelineReportsContext.Provider>
  );
});

export default PublicReportSection;
